export type CabinetType =
  | "Base"
  | "Sink Base"
  | "Upper"
  | "Tall"
  | "Corner Base"
  | "Corner Upper"
  
  | "Appliance Garage"
  | "Fridge Cabinet"
  | "Island Front"
  | "Island Back"
  | "Trash Base"
  | "Spice Base"
  | "Panel"
  | "Custom";

export interface CabinetTypeDefaults {
  label: string;
  defaultHeight: number;
  defaultDepth: number;
  standardWidths: number[];
  /** Price per linear foot for this cabinet type */
  pricePerLinearFt: number;
}

export const CABINET_TYPE_DEFAULTS: Record<CabinetType, CabinetTypeDefaults> = {
  Base: {
    label: "Base Cabinet",
    defaultHeight: 34.5,
    defaultDepth: 24,
    standardWidths: [9, 12, 15, 18, 21, 24, 27, 30, 33, 36, 42, 48],
    pricePerLinearFt: 500,
  },
  "Sink Base": {
    label: "Sink Base",
    defaultHeight: 34.5,
    defaultDepth: 24,
    standardWidths: [24, 27, 30, 33, 36, 42],
    pricePerLinearFt: 500,
  },
  Upper: {
    label: "Upper/Wall Cabinet",
    defaultHeight: 30,
    defaultDepth: 12,
    standardWidths: [9, 12, 15, 18, 21, 24, 27, 30, 33, 36, 42],
    pricePerLinearFt: 500,
  },
  Tall: {
    label: "Tall/Pantry Cabinet",
    defaultHeight: 84,
    defaultDepth: 24,
    standardWidths: [12, 15, 18, 21, 24, 30, 36],
    pricePerLinearFt: 1000,
  },
  "Corner Base": {
    label: "Corner Base",
    defaultHeight: 34.5,
    defaultDepth: 24,
    standardWidths: [33, 36, 42],
    pricePerLinearFt: 500,
  },
  "Corner Upper": {
    label: "Corner Upper",
    defaultHeight: 30,
    defaultDepth: 12,
    standardWidths: [24, 30, 36],
    pricePerLinearFt: 500,
  },
  "Appliance Garage": {
    label: "Appliance Garage",
    defaultHeight: 18,
    defaultDepth: 12,
    standardWidths: [24, 30, 36, 42, 48],
    pricePerLinearFt: 500,
  },
  "Fridge Cabinet": {
    label: "Fridge Cabinet",
    defaultHeight: 84,
    defaultDepth: 24,
    standardWidths: [30, 33, 36, 42, 48],
    // Stored as $/linear ft; edited as $/inch in Admin Pricing (÷12).
    pricePerLinearFt: 600,
  },
  "Island Front": {
    label: "Island Front",
    defaultHeight: 34.5,
    defaultDepth: 24,
    standardWidths: [12, 15, 18, 21, 24, 27, 30, 33, 36, 42, 48],
    pricePerLinearFt: 500,
  },
  "Island Back": {
    label: "Island Back",
    defaultHeight: 34.5,
    defaultDepth: 12,
    standardWidths: [12, 15, 18, 21, 24, 27, 30, 33, 36, 42, 48],
    pricePerLinearFt: 500,
  },
  "Trash Base": {
    label: "Trash/Recycling Pull-Out",
    defaultHeight: 34.5,
    defaultDepth: 24,
    standardWidths: [15, 18, 21],
    pricePerLinearFt: 500,
  },
  "Spice Base": {
    label: "Base Spice Pull-Out",
    defaultHeight: 34.5,
    defaultDepth: 24,
    standardWidths: [9, 12],
    pricePerLinearFt: 550,
  },
  Panel: {
    label: "Panel (flat / decorative)",
    defaultHeight: 30,
    defaultDepth: 0.75,
    standardWidths: [12, 15, 18, 21, 24, 30, 36, 42, 48],
    // Panels inherit the Finished Panel Rate ($/sq in). The base $/LF field
    // is not used — see pricing engine (Panel branch).
    pricePerLinearFt: 0,
  },
  Custom: {
    label: "Custom Item",
    defaultHeight: 0,
    defaultDepth: 0,
    standardWidths: [0],
    pricePerLinearFt: 0,
  },
};

export const ALL_CABINET_TYPES = Object.keys(CABINET_TYPE_DEFAULTS) as CabinetType[];

/** Cabinet types selectable in the contractor form dropdown. */
export const SELECTABLE_CABINET_TYPES: CabinetType[] = ALL_CABINET_TYPES;

// ─── Accessories ──────────────────────────────────────────────────────

export interface AccessoryDefinition {
  id: string;
  label: string;
  cabinetTypes: CabinetType[];
  minWidth?: number;
  maxWidth?: number;
  requiresStandardDepth?: boolean;
  description?: string;
  /** Fixed price per unit */
  price: number;
  /** Conflict group — only one accessory per group can be selected */
  conflictGroup?: string;
  /** Accessory lives inside a shallow top drawer */
  shallowDrawerInsert?: boolean;
  /** Accessory lives inside a deep (bottom) drawer */
  deepDrawerInsert?: boolean;
  /** Accessory requires door+drawer config (lives behind the door) */
  requiresDoorDrawer?: boolean;
  /** If true, shows a text input for the contractor to describe what they want */
  isCustomRequest?: boolean;
  /** If true, shows a left/right side picker when selected */
  requiresSideSelection?: boolean;
  /** If true, price is computed dynamically from cabinet dimensions (sqft) */
  isDynamicPanelPrice?: boolean;
  /** If true, the contractor can add this accessory more than once per cabinet */
  allowMultiple?: boolean;
  /** Maximum times it can be added when allowMultiple is on (default 10) */
  maxQuantity?: number;
}

/**
 * Suggested conflict-group keys surfaced in the admin Accessory editor dropdown
 * even before any accessory uses them. Pick one whenever two accessories on the
 * same cabinet would force the shop to call the customer to clarify.
 */
export const SUGGESTED_CONFLICT_GROUPS: { key: string; description: string }[] = [
  { key: "corner_base_organizer", description: "Lazy susan / blind-corner / magic corner" },
  { key: "countertop_door", description: "Aventos lift / pocket doors on appliance garage" },
  { key: "finished_end", description: "Decorative vs smooth finished end panel" },
  { key: "trash_pullout_config", description: "Single vs double trash pull-out" },
  { key: "base_pullout", description: "Trash, recycle, tray, spice pull-out (full base interior)" },
  { key: "drawer_insert_shallow", description: "Knife block, spice, cutlery, utensil tray (top drawer)" },
  { key: "drawer_insert_deep", description: "Pots-and-pans pegboard, dish organizer, deep cutlery (deep drawer)" },
  { key: "sink_base_storage", description: "Tilt-out sink tray, under-sink caddy" },
  { key: "wall_interior", description: "Plate rack, stemware rack, door-mounted spice rack" },
  { key: "pantry_interior", description: "Pull-out shelves, swing-out pantry, door-mounted rack" },
  { key: "toe_kick", description: "Toe-kick drawer, heater grille, lighting (same 4\" strip)" },
  { key: "lighting_under", description: "LED puck, strip, hardwired bar (one per run)" },
];

export const ACCESSORIES: AccessoryDefinition[] = [
  // ── Base Cabinet Accessories ──
  {
    id: "rollout_shelf",
    label: "Roll-Out Tray",
    cabinetTypes: ["Base", "Island Front"],
    minWidth: 12,
    description: "Full-extension roll-out shelf",
    price: 200,
    requiresDoorDrawer: true,
  },
  {
    id: "utensil_divider",
    label: "Utensil / Cutlery Divider",
    cabinetTypes: ["Base", "Island Front"],
    minWidth: 15,
    description: "Custom wood drawer organizer insert — top (shallow) drawer",
    price: 200,
    shallowDrawerInsert: true,
  },
  {
    id: "knife_block",
    label: "Knife Block Insert",
    cabinetTypes: ["Base", "Island Front"],
    minWidth: 15,
    description: "In-drawer wood knife block — top (shallow) drawer",
    price: 200,
    shallowDrawerInsert: true,
  },
  {
    id: "deep_drawer_pegs",
    label: "Deep Drawer Peg System",
    cabinetTypes: ["Base", "Island Front"],
    minWidth: 18,
    description: "Adjustable pegs for plates & bowls — deep (bottom) drawers",
    price: 150,
    deepDrawerInsert: true,
  },
  {
    id: "tray_divider",
    label: "Tray Divider",
    cabinetTypes: ["Base", "Island Front", "Upper", "Tall"],
    minWidth: 9,
    maxWidth: 12,
    description: "Vertical tray divider for narrow one-door cabinets",
    price: 25,
    allowMultiple: true,
    maxQuantity: 10,
  },
  {
    id: "sink_tipout",
    label: "Sink Tip-Out Trays",
    cabinetTypes: ["Sink Base"],
    minWidth: 24,
    description: "False-panel tip-out trays in front of sink",
    price: 75,
  },
  {
    id: "mixer_lift",
    label: "Mixer Lift",
    cabinetTypes: ["Base"],
    minWidth: 18,
    requiresStandardDepth: true,
    description: "Heavy-duty pop-up mixer shelf",
    price: 175,
    requiresDoorDrawer: true,
  },
  {
    id: "charging_dock",
    label: "Charging Dock",
    cabinetTypes: ["Base", "Upper"],
    minWidth: 12,
    description: "Built-in device charging station",
    price: 500,
  },

  // ── Corner Cabinet Accessories ──
  {
    id: "smart_susan",
    label: "Smart Susan",
    cabinetTypes: ["Corner Base"],
    minWidth: 33,
    description: "Rotating corner organizer",
    price: 385,
    conflictGroup: "corner_base_organizer",
  },
  {
    id: "peanut_pullout",
    label: "Blind Corner Peanut Pull-Out",
    cabinetTypes: ["Corner Base"],
    minWidth: 33,
    description: "Swing-out blind corner pull-out system",
    price: 485,
    conflictGroup: "corner_base_organizer",
  },
  {
    id: "magic_corner",
    label: "Magic Corner",
    cabinetTypes: ["Corner Base"],
    minWidth: 33,
    description: "Premium two-tier swing-out corner system",
    price: 950,
    conflictGroup: "corner_base_organizer",
  },
  {
    id: "wall_susan",
    label: "Wall Susan",
    cabinetTypes: ["Corner Upper"],
    description: "Rotating corner organizer for wall cabinets",
    price: 450,
  },

  // ── Tall Cabinet Accessories ──
  {
    id: "chefs_pantry",
    label: "Chef's Pantry Pull-Out",
    cabinetTypes: ["Tall"],
    minWidth: 18,
    description: "Full-height single pull-out wire rack system",
    price: 1500,
  },
  {
    id: "tall_spice_rack",
    label: "Door-Mounted Spice Rack (Tall)",
    cabinetTypes: ["Tall"],
    description: "Wood racks on inside of tall doors — $550 per rack",
    price: 550,
  },

  // ── Upper Cabinet Accessories ──
  {
    id: "upper_spice_rack",
    label: "Door-Mounted Spice Rack",
    cabinetTypes: ["Upper"],
    minWidth: 12,
    maxWidth: 24,
    description: "Inside-door spice organizer",
    price: 275,
  },
  {
    id: "stemware_rack",
    label: "Stemware Rack",
    cabinetTypes: ["Upper"],
    minWidth: 12,
    description: "Under-shelf wine glass holder",
    price: 375,
  },

  // ── Appliance Garage ──
  {
    id: "aventos_lift",
    label: "Lift-Up Door (Aventos)",
    cabinetTypes: ["Appliance Garage"],
    description: "Door swings up and out of the way",
    price: 250,
    conflictGroup: "countertop_door",
  },
  {
    id: "pocket_doors",
    label: "Pocket Doors",
    cabinetTypes: ["Appliance Garage"],
    description: "Doors slide back into cabinet walls",
    price: 250,
    conflictGroup: "countertop_door",
  },

  // ── Finished End Panel (next to appliance) ──
  {
    id: "appliance_end",
    label: "Finished End Panel (Next to Appliance)",
    cabinetTypes: ["Base", "Sink Base", "Island Front"],
    description: "4\" finished panel on the appliance side — select left or right",
    price: 200,
    /** Requires a side selection: "left" or "right" */
    requiresSideSelection: true,
  },

  // ── Trash Base ──
  {
    id: "trash_pullout_single",
    label: "Single Trash Pull-Out (Full Base Height)",
    cabinetTypes: ["Trash Base"],
    description: "One-bin pull-out — full base cabinet height, no drawer above",
    price: 325,
    conflictGroup: "trash_pullout_config",
  },
  {
    id: "trash_pullout_double",
    label: "Double Trash Pull-Out (With Drawer Above)",
    cabinetTypes: ["Trash Base"],
    description: "Two-bin pull-out with one drawer above (15\"–21\" wide)",
    price: 425,
    conflictGroup: "trash_pullout_config",
  },

  // ── Finished End Panels ──
  {
    id: "finished_end_decorative",
    label: "Finished End Panel (Decorative)",
    cabinetTypes: ["Base", "Sink Base", "Corner Base", "Upper", "Island Front", "Island Back"],
    description: "Decorative finished end panel — priced per sq ft from cabinet dimensions",
    price: 0,
    isDynamicPanelPrice: true,
    conflictGroup: "finished_end",
    requiresSideSelection: true,
  },
  {
    id: "finished_end_smooth",
    label: "Finished End Panel (Smooth)",
    cabinetTypes: ["Base", "Sink Base", "Corner Base", "Upper", "Island Front", "Island Back"],
    description: "Smooth finished end panel — priced per sq ft from cabinet dimensions",
    price: 0,
    isDynamicPanelPrice: true,
    conflictGroup: "finished_end",
    requiresSideSelection: true,
  },

  // ── Universal ──
  {
    id: "glass_door",
    label: "Glass Door Insert",
    cabinetTypes: ["Upper", "Corner Upper"],
    description: "Replace panel with glass insert",
    price: 0, // priced per project
  },
  {
    id: "custom_request",
    label: "Custom Accessory Request",
    cabinetTypes: ["Base", "Sink Base", "Upper", "Tall", "Corner Base", "Corner Upper", "Island Front", "Island Back"],
    description: "Add a custom accessory — enter your own description and price",
    price: 0,
    isCustomRequest: true,
  },
];

export type FrontConfig =
  | "door_drawer"
  | "door_only"
  | "three_drawer"
  | "four_drawer_equal"
  | "four_drawer_bottom_larger"
  | "island_back_cabinet"
  | "island_back_paneled"
  | "panel_flat"
  | "panel_decorative";

// ─── Corner Cabinet Config ────────────────────────────────────────────

export type CornerStyle = "blind" | "diagonal" | "l_shape" | "easy_reach";
export type BlindSide = "left" | "right";

export interface CornerStyleOption {
  value: CornerStyle;
  label: string;
  /** Standard widths offered for this corner sub-type. */
  widths: number[];
  /** Short helper text shown under the picker. */
  description?: string;
}

/** Available corner sub-types for Corner Base and Corner Upper. */
export const CORNER_STYLE_OPTIONS: Record<"Corner Base" | "Corner Upper", CornerStyleOption[]> = {
  "Corner Base": [
    { value: "blind", label: "Blind Corner", widths: [33, 36, 42], description: "One side hidden behind adjacent run — door + filler on the blind side." },
    { value: "diagonal", label: "Diagonal (45°)", widths: [36, 39, 42], description: "Angled face spanning both walls — commonly houses a lazy susan." },
    { value: "l_shape", label: "L-Shape / 90°", widths: [36, 39, 42], description: "Two doors meeting at the corner — full access on both sides." },
    { value: "easy_reach", label: "Easy-Reach / Pie-Cut", widths: [36, 39, 42], description: "Single bi-fold pie-cut door." },
  ],
  "Corner Upper": [
    { value: "blind", label: "Blind Corner", widths: [24, 30, 36], description: "One side hidden behind adjacent wall cabinet." },
    { value: "diagonal", label: "Diagonal (45°)", widths: [24, 27, 30], description: "Angled face spanning both walls." },
  ],
};

/** Default corner sub-type when a corner cabinet is first added. */
export const DEFAULT_CORNER_STYLE: CornerStyle = "blind";

export function getCornerStyleLabel(type: CabinetType, style?: string): string | null {
  if (type !== "Corner Base" && type !== "Corner Upper") return null;
  const opt = CORNER_STYLE_OPTIONS[type].find((o) => o.value === (style ?? DEFAULT_CORNER_STYLE));
  return opt?.label ?? null;
}

/**
 * Which corner-organizer accessory IDs are valid for a given corner sub-type.
 * Returns null when the cabinet is not a corner.
 */
function getAllowedCornerOrganizerIds(type: CabinetType, style?: string): Set<string> | null {
  if (type === "Corner Base") {
    switch ((style ?? DEFAULT_CORNER_STYLE) as CornerStyle) {
      case "blind": return new Set(["peanut_pullout"]);
      case "diagonal": return new Set(["smart_susan", "magic_corner"]);
      case "l_shape": return new Set(["smart_susan", "magic_corner"]);
      case "easy_reach": return new Set(["smart_susan"]);
    }
  }
  if (type === "Corner Upper") {
    switch ((style ?? DEFAULT_CORNER_STYLE) as CornerStyle) {
      case "blind": return new Set();
      case "diagonal": return new Set(["wall_susan"]);
      default: return new Set();
    }
  }
  return null;
}

/** Accessory IDs that are corner-organizer-specific (filtered by sub-type). */
const CORNER_ORGANIZER_IDS = new Set(["smart_susan", "peanut_pullout", "magic_corner", "wall_susan"]);


// ─── Tall Cabinet Config ──────────────────────────────────────────────

export type TallConfig = "pantry" | "oven_2unit" | "oven_1unit";
export type OvenSubConfig = "drawers" | "doors";

/** Price per pantry roll-out (included shelves are not charged separately). */
export const PANTRY_ROLLOUT_PRICE = 200;
export const PANTRY_ROLLOUT_DEFAULT = 5;
export const PANTRY_ROLLOUT_MAX = 6;

export function getTallConfigLabel(
  cfg: TallConfig | undefined,
  ovenSub?: OvenSubConfig,
  rolloutCount?: number
): string {
  if (cfg === "pantry") {
    const n = rolloutCount ?? PANTRY_ROLLOUT_DEFAULT;
    return `Pantry — ${n} roll-out${n === 1 ? "" : "s"} + 1 shelf`;
  }
  if (cfg === "oven_2unit") return "Oven Cabinet (2-unit, drawer beneath)";
  if (cfg === "oven_1unit") {
    return ovenSub === "doors"
      ? "Oven Cabinet (1-unit, two doors)"
      : "Oven Cabinet (1-unit, two drawers)";
  }
  return "Shop-standard pantry config";
}

/** True for any front config that is all drawers (no door). */
export function isAllDrawerConfig(fc?: string): boolean {
  return fc === "three_drawer" || fc === "four_drawer_equal" || fc === "four_drawer_bottom_larger";
}

/** Number of drawers implied by the front config. */
export function getDrawerCount(fc?: string): number {
  if (fc === "door_drawer") return 1;
  if (fc === "three_drawer") return 3;
  if (fc === "four_drawer_equal" || fc === "four_drawer_bottom_larger") return 4;
  return 0;
}

/**
 * Which front-style configs the contractor may choose for a given cabinet type.
 * Returns [] when the front style is hidden / fixed by the shop and the contractor has no input.
 */
export function getAllowedFrontConfigs(type: CabinetType): FrontConfig[] {
  switch (type) {
    case "Base":
    case "Island Front":
      return ["door_drawer", "door_only", "three_drawer", "four_drawer_equal", "four_drawer_bottom_larger"];
    case "Island Back":
      return ["island_back_cabinet", "island_back_paneled"];
    default:
      // Sink Base: locked to door + false drawer front (sink basin blocks real drawers).
      // Upper, Tall, Corner Upper, Appliance Garage, Trash Base: no contractor input on front style.
      return [];
  }
}

/** Human-readable description of the front-style for a cabinet (display only). */
export function getFrontConfigLabel(
  type: CabinetType,
  fc: string | undefined,
  widthInches: number,
  hingeSide?: string
): string {
  if (fc === "door_only") {
    return widthInches <= 24 ? `1 Door — hinge ${hingeSide || "left"}` : "2 Doors (pair)";
  }
  if (fc === "three_drawer") return "3 Drawers";
  if (fc === "four_drawer_equal") return "4 Drawers (equal)";
  if (fc === "four_drawer_bottom_larger") return "4 Drawers (top 3 equal, bottom larger)";
  if (fc === "island_back_paneled") return "Paneled back";
  if (fc === "island_back_cabinet") return "Cabinet back (doors)";
  if (fc === "panel_flat") return "Flat panel";
  if (fc === "panel_decorative") return "Decorative panel";

  switch (type) {
    case "Sink Base":
      return "Door + false drawer front (sink)";
    case "Tall":
      return "Shop-standard pantry config";
    case "Trash Base":
      return "Trash / recycling pull-out";
    case "Island Back":
      return "Cabinet back (doors)";
    case "Appliance Garage":
      return "Appliance garage door";
    case "Fridge Cabinet":
      return "Fridge surround (no doors/drawers)";
    case "Panel":
      return "Flat / decorative panel (no doors)";
    case "Upper":
    case "Corner Upper":
      return widthInches <= 24 ? `1 Door — hinge ${hingeSide || "left"}` : "2 Doors (pair)";
    default:
      return widthInches <= 24
        ? `1 Drawer + 1 Door — hinge ${hingeSide || "left"}`
        : "1 Drawer + 2 Doors (pair)";
  }
}

export function getFilteredAccessories(
  cabinetType: CabinetType,
  widthInches: number,
  depthInches: number,
  frontConfig?: FrontConfig,
  cornerStyle?: string,
): AccessoryDefinition[] {
  const defaults = CABINET_TYPE_DEFAULTS[cabinetType];
  const isStandardDepth = depthInches === defaults.defaultDepth;

  // Island Back and Panel cabinets do not support any accessories
  if (cabinetType === "Island Back" || cabinetType === "Panel") return [];

  const allowedCornerOrgs = getAllowedCornerOrganizerIds(cabinetType, cornerStyle);

  return ACCESSORIES.filter((acc) => {
    if (!acc.cabinetTypes.includes(cabinetType)) return false;
    if (acc.minWidth && widthInches < acc.minWidth) return false;
    if (acc.maxWidth && widthInches > acc.maxWidth) return false;
    if (acc.requiresStandardDepth && !isStandardDepth) return false;
    // Door+drawer accessories unavailable on all-drawer configs
    if (acc.requiresDoorDrawer && isAllDrawerConfig(frontConfig)) return false;
    // Door-only: no drawers exist, so drawer inserts are unavailable
    if (frontConfig === "door_only" && (acc.shallowDrawerInsert || acc.deepDrawerInsert)) return false;
    // Corner organizers: keep only those valid for the chosen corner sub-type
    if (allowedCornerOrgs && CORNER_ORGANIZER_IDS.has(acc.id) && !allowedCornerOrgs.has(acc.id)) return false;
    return true;
  });
}


/**
 * Given selected accessory IDs and front config, returns blocked IDs.
 *
 * Rules:
 * - Static conflict groups (corner organizers, countertop doors)
 * - 1-drawer (door_drawer): shallow inserts share one slot; deep inserts blocked entirely
 * - 3-drawer: no drawer-slot restrictions
 */
export function getConflictingAccessoryIds(
  selectedIds: string[],
  frontConfig?: FrontConfig
): Set<string> {
  const blocked = new Set<string>();

  // ── Static conflict groups ──
  const claimedGroups = new Map<string, string>();
  for (const id of selectedIds) {
    const acc = ACCESSORIES.find((a) => a.id === id);
    if (acc?.conflictGroup) {
      claimedGroups.set(acc.conflictGroup, id);
    }
  }
  for (const acc of ACCESSORIES) {
    if (acc.conflictGroup && claimedGroups.has(acc.conflictGroup) && claimedGroups.get(acc.conflictGroup) !== acc.id) {
      blocked.add(acc.id);
    }
  }

  // ── Shallow-drawer inserts: always mutually exclusive (one top drawer) ──
  const selectedShallow = selectedIds.find((id) => {
    const acc = ACCESSORIES.find((a) => a.id === id);
    return acc?.shallowDrawerInsert;
  });
  if (selectedShallow) {
    for (const acc of ACCESSORIES) {
      if (acc.shallowDrawerInsert && acc.id !== selectedShallow) {
        blocked.add(acc.id);
      }
    }
  }

  // ── Deep-drawer inserts: blocked only for door+drawer (no deep drawers) ──
  if (frontConfig === "door_drawer") {
    for (const acc of ACCESSORIES) {
      if (acc.deepDrawerInsert) {
        blocked.add(acc.id);
      }
    }
  }

  return blocked;
}

// ─── Wood Species & Upcharges ─────────────────────────────────────────

export interface WoodSpeciesOption {
  name: string;
  /** Multiplier on the cabinet base price (1.0 = no upcharge) */
  priceMultiplier: number;
}

export const WOOD_SPECIES_OPTIONS: WoodSpeciesOption[] = [
  { name: "Maple", priceMultiplier: 1.0 },
  { name: "Cherry", priceMultiplier: 1.0 },
  { name: "Hickory", priceMultiplier: 1.0 },
  { name: "White Oak", priceMultiplier: 1.5 },
  { name: "Walnut", priceMultiplier: 2.0 },
];

/** Flat list of species names for dropdowns */
export const WOOD_SPECIES = WOOD_SPECIES_OPTIONS.map((w) => w.name);

export function getSpeciesMultiplier(species: string): number {
  return WOOD_SPECIES_OPTIONS.find((w) => w.name === species)?.priceMultiplier ?? 1.0;
}

// ─── Finishes ─────────────────────────────────────────────────────────

export const FINISHES = [
  "Painted",
  "Stained",
];

// ─── Door Styles ──────────────────────────────────────────────────────

export const DOOR_STYLES = [
  "Shaker",
  "Raised Panel",
  "Slab",
  "Beadboard",
  "Recessed Panel",
  "Mullion/Glass",
  "Arch Top",
  "Custom",
];

// ─── Construction Types ───────────────────────────────────────────────

export const CONSTRUCTION_TYPES = ["Overlay", "Inset"] as const;
export type ConstructionType = (typeof CONSTRUCTION_TYPES)[number];

// ─── Door Count Logic ─────────────────────────────────────────────────

export function getDoorCount(widthInches: number): number {
  return widthInches <= 24 ? 1 : 2;
}

// ─── Extras & Trim ────────────────────────────────────────────────────

export interface ExtraTypeDefinition {
  id: string;
  label: string;
  unit: string;
  pricePerUnit: number;
  /** If true, apply species upcharge multiplier */
  applySpeciesUpcharge?: boolean;
}

export const EXTRA_TYPES: ExtraTypeDefinition[] = [
  { id: "crown_moulding", label: "Crown Moulding", unit: "linear ft", pricePerUnit: 20 },
  { id: "frieze", label: "Frieze", unit: "linear ft", pricePerUnit: 20 },
  { id: "base_moulding", label: "Base Moulding", unit: "linear ft", pricePerUnit: 20 },
  { id: "light_rail", label: "Light Rail", unit: "linear ft", pricePerUnit: 20 },
  { id: "hood", label: "Range Hood", unit: "inches (width)", pricePerUnit: 72 },
  { id: "floating_shelves", label: "Floating Shelves (Painted)", unit: "sq ft", pricePerUnit: 120 },
  { id: "floating_shelves_stained", label: "Floating Shelves (Stained)", unit: "sq ft", pricePerUnit: 120, applySpeciesUpcharge: true },
  { id: "posts", label: "Island Posts", unit: "count", pricePerUnit: 375 },
];
